/* AdminNavbar - Admin navigation with full-width active underline on the selected navigation link.
   Updated: Added Transactiemails (Brevo mail route) under Commercieel. */

import { useNavigate, useLocation } from 'react-router-dom';
import { ShoppingBag, Package, FileText, Receipt, ChevronDown, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { hasAccess, type AdminRole } from '@/lib/supabase';

interface NavItem {
  label: string;
  href: string;
  module?: string;
}

interface NavGroup {
  label: string;
  icon: React.ElementType;
  items: NavItem[];
  matchPaths: string[];
}

interface AdminNavbarProps {
  adminRole: string;
}

function getNavGroups(role: string): NavGroup[] {
  const isSuperadmin = role === 'superadmin';

  const groups: NavGroup[] = [
    {
      label: 'Catalogus',
      icon: Package,
      matchPaths: ['/kh-admin/kortingscodes', '/kh-admin/reviews', '/kh-admin/plant-pricing', '/kh-admin/default-margins', '/kh-admin/subcategories'],
      items: [
        { label: 'Subcategorieën', href: '/kh-admin/subcategories', module: 'assortiment' },
        { label: 'Standaard margetabellen', href: '/kh-admin/default-margins', module: 'settings' },
        { label: 'Prijscalculator', href: '/kh-admin/plant-pricing', module: 'settings' },
        { label: 'Kortingscodes', href: '/kh-admin/kortingscodes', module: 'coupons' },
        { label: 'Reviews', href: '/kh-admin/reviews', module: 'settings' },
      ],
    },
    {
      label: 'Content',
      icon: FileText,
      matchPaths: ['/kh-admin/blogs', '/kh-admin/klantenservice', '/kh-admin/faq'],
      items: [
        { label: 'Blogs', href: '/kh-admin/blogs', module: 'blogs' },
        ...(isSuperadmin ? [{ label: 'Blog Automatisering', href: '/kh-admin/blogs/automatisering', module: 'blog_automation' }] : []),
        { label: 'Klantenservice', href: '/kh-admin/klantenservice', module: 'faq' },
        { label: 'Veelgestelde vragen', href: '/kh-admin/faq', module: 'faq' },
      ],
    },
    {
      label: 'Commercieel',
      icon: Receipt,
      matchPaths: ['/kh-admin/quote-requests', '/kh-admin/mailing', '/kh-admin/newsletter', '/kh-admin/transactiemails'],
      items: [
        { label: 'Facturen', href: '/kh-admin/orders?new=true', module: 'documents' },
        { label: 'Offertes', href: '/kh-admin/quote-requests?tab=offertes', module: 'documents' },
        { label: 'Offerteaanvragen', href: '/kh-admin/quote-requests', module: 'quote_requests' },
        { label: 'Nieuwsbrief', href: '/kh-admin/mailing', module: 'mailing' },
        { label: 'Transactiemails', href: '/kh-admin/transactiemails', module: 'settings' },
      ],
    },
    {
      label: 'Zakelijk',
      icon: Building2,
      matchPaths: ['/kh-admin/zakelijk'],
      items: [
        { label: 'Zakelijke klanten', href: '/kh-admin/zakelijk/klanten', module: 'business' },
        { label: 'Prijslijst', href: '/kh-admin/zakelijk/prijslijst', module: 'business' },
        { label: 'Zakelijke bestellingen', href: '/kh-admin/zakelijk/bestellingen', module: 'business' },
      ],
    },
  ];

  return groups.map(group => ({
    ...group,
    items: group.items.filter(item =>
      !item.module || hasAccess(role as AdminRole, item.module)
    ),
  })).filter(group => group.items.length > 0);
}

function isGroupActive(pathname: string, group: NavGroup): boolean {
  return group.matchPaths.some(p => pathname.startsWith(p));
}

export default function AdminNavbar({ adminRole }: AdminNavbarProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const groups = getNavGroups(adminRole);
  const ordersActive = pathname === '/kh-admin/orders';

  return (
    <nav className="hidden md:flex items-center justify-center flex-1 gap-1">
      <a
        href="/kh-admin/orders"
        onClick={(e) => { e.preventDefault(); navigate('/kh-admin/orders'); }}
        className={`relative flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
          ordersActive
            ? 'text-[#1e2b22] bg-[#e7e1d6]/55'
            : 'text-[#5a6552] hover:text-[#1e2b22] hover:bg-[#e7e1d6]/50'
        }`}
      >
        <ShoppingBag className="h-4 w-4" />
        <span>Bestellingen</span>
        {ordersActive && (
          <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1e2b22] rounded-full" />
        )}
      </a>

      {hasAccess(adminRole as AdminRole, 'assortiment') && (
        <a
          href="/kh-admin/assortiment"
          onClick={(e) => { e.preventDefault(); navigate('/kh-admin/assortiment'); }}
          className={`relative flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
            pathname.startsWith('/kh-admin/assortiment')
              ? 'text-[#1e2b22] bg-[#e7e1d6]/55'
              : 'text-[#5a6552] hover:text-[#1e2b22] hover:bg-[#e7e1d6]/50'
          }`}
        >
          <Package className="h-4 w-4" />
          <span>Assortiment</span>
          {pathname.startsWith('/kh-admin/assortiment') && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1e2b22] rounded-full" />
          )}
        </a>
      )}

      {groups.map((group) => {
        const active = isGroupActive(pathname, group);
        const Icon = group.icon;

        return (
          <DropdownMenu key={group.label}>
            <DropdownMenuTrigger asChild>
              <button
                className={`relative flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors outline-none ${
                  active
                    ? 'text-[#1e2b22] bg-[#e7e1d6]/55'
                    : 'text-[#5a6552] hover:text-[#1e2b22] hover:bg-[#e7e1d6]/50'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{group.label}</span>
                <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                {active && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1e2b22] rounded-full" />
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52 border-[#e7e1d6] bg-[#fcfaf6]">
              <DropdownMenuLabel className="text-xs font-medium uppercase tracking-wider text-[#5a6552]">
                {group.label}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {group.items.map((item) => {
                const itemActive = pathname === item.href || pathname.startsWith(item.href + '/');
                return (
                  <DropdownMenuItem
                    key={item.href}
                    onClick={() => navigate(item.href)}
                    className={`cursor-pointer ${
                      itemActive ? 'bg-[#e7e1d6]/55 text-[#1e2b22] font-medium' : 'text-[#1a1a1a] hover:bg-[#e7e1d6]/50 hover:text-[#1e2b22]'
                    }`}
                  >
                    {item.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      })}
    </nav>
  );
}

export { getNavGroups, type NavGroup, type NavItem };